import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BasicModal from '__components/Modals/presets/BasicModal';
import { useHistory } from 'react-router-dom';
import {
  SMART_CONNECTOR_TYPES,
  SMARTCONNECTORS_PATHNAME,
} from '__pages/SmartConnectors/constants';
import Select from '__components/Inputs/Select';
import { GenericOption, SmartConnector } from '__pages/SmartConnectors/types';
import TextInput from '__components/Inputs/TextInput';
import SmartConnectorService from '__services/SmartConnectorService';
import CustomObjectsService from '__services/CustomObjectsService';
import { getOriginalError } from '__services/AxiosService';
import { useToast } from '__components/ToastProvider';
import { getErrorMessage } from '__hooks/useErrors';
import { useFlashTransition } from '__hooks/useFlashState';
import { FORCE_ALL_RECORDS_SIZE } from '__services/helpers';
import { useAsync } from 'react-use';
import { toastVariant } from '__components/ToastProvider';
import { getDataQAForInput } from '__components/Inputs/helpers';

const errorsOrder: Partial<Record<keyof SmartConnector, number>> = {
  name: 0,
  custom_object: 1,
};

const useErrors = () => {
  const [errors, setErrors] = useState<
    Partial<Record<keyof SmartConnector, string>>
  >({
    name: '',
    custom_object: '',
  });

  const [message, showMessage, flashErrorMessage, cancelFlash] =
    useFlashTransition();

  const firstErrorKey = (Object.keys(errors) as (keyof SmartConnector)[]).sort(
    (a, b) => errorsOrder[a]! - errorsOrder[b]!
  )[0];

  useEffect(() => {
    if (firstErrorKey) {
      flashErrorMessage(errors[firstErrorKey]);
    }
  }, [errors, firstErrorKey, flashErrorMessage]);

  const clearError = useCallback(
    (errorKey: keyof SmartConnector) => {
      if (showMessage) {
        cancelFlash();
      }

      if (firstErrorKey) {
        setErrors((prev) => {
          const { [errorKey]: _, ...rest } = prev;
          return rest;
        });
      }
    },
    [showMessage, cancelFlash, firstErrorKey, setErrors]
  );

  const validation = useMemo(
    () => ({
      name: firstErrorKey === 'name' && { showMessage, message },
      custom_object: firstErrorKey === 'custom_object' && {
        showMessage,
        message,
      },
    }),
    [showMessage, message, firstErrorKey]
  );

  return {
    errors,
    setErrors,
    validation,
    clearError,
  };
};

type CreateSmartConnectorModalProps = {
  onHide: () => void;
};

export const CreateSmartConnectorModal: React.FC<
  CreateSmartConnectorModalProps
> = ({ onHide }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [showToast] = useToast();

  const [name, setName] = useState('');
  const [selectedObject, setSelectedObject] = useState<GenericOption | null>(
    null
  );
  const [submitting, setSubmitting] = useState(false);
  const { errors, setErrors, validation, clearError } = useErrors();

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      const { data } = await SmartConnectorService.createSmartConnector({
        name: name.trim(),
        connector_type: SMART_CONNECTOR_TYPES.spreadsheet,
        custom_object: selectedObject?.value,
        is_diff_check_enabled: true,
      });
      history.push(`${SMARTCONNECTORS_PATHNAME}/${data.id}/settings`);
    } catch (e) {
      const orig = getOriginalError(e);
      if (orig?.name || orig?.custom_object) {
        setErrors({
          name: orig.name || '',
          custom_object: orig.custom_object || '',
        });
      } else {
        showToast({
          message:
            getErrorMessage(orig) || t('Failed to create SmartConnector'),
          variant: toastVariant.FAILURE,
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleChangeObject = (value: GenericOption) => {
    setSelectedObject(value);
    clearError('custom_object');
  };

  const handleChangeName = (value: string) => {
    setName(value);
    clearError('name');
  };

  const { value: customObjects = [], loading: loadingObjects } =
    useAsync(async () => {
      const { results } = await CustomObjectsService.getCustomObjectList({
        size: FORCE_ALL_RECORDS_SIZE,
        customOnly: false,
      });
      return results.map(
        ({ id, objectName }: { id: string; objectName: string }) => ({
          value: id,
          label: objectName,
        })
      );
    }, []);

  return (
    <BasicModal
      heading={t('Add SmartConnector')}
      onConfirm={handleConfirm}
      onHide={onHide}
      buttonText={t('Save')}
      defaultLeftBtnText={t('Cancel')}
      show
      disabled={
        !selectedObject ||
        !name.trim() ||
        submitting ||
        Object.values(errors).some(Boolean)
      }
    >
      <TextInput
        label={t('SmartConnector Name')}
        placeholder={t('Enter Name')}
        value={name}
        onChange={handleChangeName}
        validate={validation['name']}
        error={errors.name}
        {...getDataQAForInput('smart-connector-name', 'text')}
        inModal
        margin
      />
      <Select
        label={t('Choose Primary Object')}
        placeholder={t('Choose Object')}
        value={selectedObject}
        onChange={handleChangeObject}
        options={customObjects}
        validate={validation['custom_object']}
        error={errors.custom_object}
        loadItems={loadingObjects}
        {...getDataQAForInput('smart-connector-context-object', 'dropdown')}
        menuInline
      />
    </BasicModal>
  );
};
